import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import PageHero from "@/components/aevos/PageHero";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pushGtmEvent } from "@/lib/gtm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PARTNER_TYPES = [
  "Estúdio / Academia",
  "Marca / Indústria",
  "Influenciador(a)",
  "Recursos Humanos / Empresa",
  "Médico(a) / Profissional de saúde",
  "Outro",
];

const PARTNERSHIP_MODELS = [
  { t: "Estúdios e academias", d: "Padrão clínico de avaliação para sua base de alunos com benefícios institucionais." },
  { t: "Marcas e indústria", d: "Co-criação de conteúdos educativos baseados em ciência e autoridade médica." },
  { t: "Recursos Humanos", d: "Programas corporativos de saúde metabólica para times de alta performance." },
  { t: "Influenciadores", d: "Curadoria clínica AEVOS aplicada à comunicação ao seu público." },
  { t: "Profissionais de saúde", d: "Encaminhamentos cruzados e protocolos clínicos integrados." },
];

const PartnershipInfoPanel = () => (
  <div className="lg:col-span-2">
    <div className="aevos-eyebrow mb-3">Para quem é</div>
    <h2 className="font-display font-bold text-3xl text-black leading-tight">Modelos de parceria.</h2>
    <ul className="mt-6 space-y-4">
      {PARTNERSHIP_MODELS.map((p) => (
        <li key={p.t} className="flex gap-3 items-start">
          <CheckCircle2 size={18} className="text-[#00C4FF] mt-1 flex-shrink-0"/>
          <div>
            <p className="font-display font-semibold text-black">{p.t}</p>
            <p className="text-sm text-[#333]">{p.d}</p>
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const PartnershipSuccessCard = ({ firstName }) => (
  <div className="aevos-card p-10 text-center" data-testid="partnership-success">
    <div className="w-14 h-14 rounded-full bg-[#E6F8FF] border border-[#00C4FF] inline-flex items-center justify-center">
      <CheckCircle2 size={24} className="text-[#00C4FF]"/>
    </div>
    <h3 className="font-display font-bold text-2xl text-black mt-5">Proposta recebida.</h3>
    <p className="mt-3 text-[#333] max-w-md mx-auto">
      Obrigado, {firstName}. A equipe AEVOS vai analisar sua proposta e entrar em contato em breve pelo WhatsApp informado.
    </p>
  </div>
);

const PartnershipFormCard = ({ form, setForm, loading, onSubmit }) => (
  <form onSubmit={onSubmit} className="aevos-card p-8" data-testid="partnership-form">
    <div className="aevos-eyebrow mb-3">Formulário</div>
    <h3 className="font-display font-bold text-2xl text-black">Conte-nos sobre sua proposta.</h3>

    <div className="mt-6 grid md:grid-cols-2 gap-4">
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">Seu nome</Label>
        <Input data-testid="partnership-input-name" required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})} className="mt-1" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">Tipo de parceiro</Label>
        <Select value={form.partner_type} onValueChange={(v)=>setForm({...form, partner_type:v})}>
          <SelectTrigger className="mt-1" data-testid="partnership-input-type"><SelectValue placeholder="Selecione"/></SelectTrigger>
          <SelectContent>
            {PARTNER_TYPES.map((t) => (
              <SelectItem key={t} value={t} data-testid={`partnership-type-${t.split(" ")[0].toLowerCase()}`}>{t}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">Empresa / canal</Label>
        <Input data-testid="partnership-input-company" required value={form.company} onChange={(e)=>setForm({...form, company:e.target.value})} className="mt-1" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">Tamanho da base</Label>
        <Input data-testid="partnership-input-audience" placeholder="Ex.: 500 alunos, 50k seguidores" value={form.audience_size} onChange={(e)=>setForm({...form, audience_size:e.target.value})} className="mt-1" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">WhatsApp</Label>
        <Input data-testid="partnership-input-whatsapp" required placeholder="(11) 9 9999-9999" value={form.whatsapp} onChange={(e)=>setForm({...form, whatsapp:e.target.value})} className="mt-1" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">E-mail</Label>
        <Input data-testid="partnership-input-email" type="email" required value={form.email} onChange={(e)=>setForm({...form, email:e.target.value})} className="mt-1" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">Proposta</Label>
        <Textarea data-testid="partnership-input-proposal" required rows={5} value={form.proposal} onChange={(e)=>setForm({...form, proposal:e.target.value})} className="mt-1" placeholder="Descreva brevemente o que você imagina para essa parceria." />
      </div>
    </div>

    <button type="submit" disabled={loading} className="mt-7 btn-primary" data-testid="partnership-submit">
      {loading ? (<><Loader2 size={16} className="animate-spin"/> Enviando...</>) : (<>Enviar proposta <ArrowRight size={16}/></>)}
    </button>
  </form>
);

const Parcerias = () => {
  const [form, setForm] = useState({
    name: "", partner_type: "", company: "", audience_size: "",
    proposal: "", whatsapp: "", email: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.partner_type) {
      toast.error("Selecione o tipo de parceiro.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads/partnership`, form);
      setSent(true);
      pushGtmEvent("partnership_form_submit", { partner_type: form.partner_type });
      toast.success("Proposta enviada com sucesso.");
    } catch (err) {
      toast.error("Não foi possível enviar agora. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="page-parcerias">
      <PageHero
        eyebrow="Parcerias B2B"
        title="Conecte sua audiência a um padrão clínico AEVOS."
        subtitle="Programas customizados para estúdios, academias, marcas, recursos humanos, influenciadores e profissionais de saúde."
      />

      <section className="section-pad">
        <div className="aevos-container grid lg:grid-cols-5 gap-10">
          <PartnershipInfoPanel />
          <div className="lg:col-span-3">
            {sent ? (
              <PartnershipSuccessCard firstName={form.name.split(" ")[0]} />
            ) : (
              <PartnershipFormCard form={form} setForm={setForm} loading={loading} onSubmit={submit} />
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Parcerias;
